import React, { useEffect, useState } from "react";
import { Globe, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { formatDateTime, formatNumber, formatMoney, formatPhone, convertMoney } from "@/utils/formatters";

export function LocaleButton({ className = "", testid = "locale-open-btn" }) {
  const { lang, country, t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label={t("locale.open")} title={t("locale.open")} data-testid={testid}
              className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-full border border-stone-200 bg-white hover:border-stone-300 text-[12px] font-bold text-stone-700 transition-colors ${className}`}>
        <Globe size={14} className="text-stone-500" />
        <span data-testid="locale-current">{lang.toUpperCase()} · {country}</span>
      </button>
      <LocaleDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function LanguageList() {
  const { lang, setLang, languages, t } = useI18n();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t("locale.language")}>
      {Object.entries(languages).map(([code, l]) => (
        <button key={code} role="radio" aria-checked={lang === code} onClick={() => setLang(code)} lang={code} dir={l.dir}
                data-testid={`locale-lang-${code}`}
                className={`text-start rounded-2xl border px-3 py-2 transition-colors ${lang === code ? "border-[#2D5A27] bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}>
          <div className="flex items-center gap-2">
            <span className="font-bold text-stone-900">{l.native}</span>
            <span className="text-[11px] text-stone-500">{l.name}</span>
            {lang === code && <Check size={14} className="ms-auto text-[#2D5A27]" />}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            <span data-testid={`locale-status-${code}`}
                  className={`text-[10px] font-black uppercase tracking-wide rounded-full px-2 py-0.5 ${l.status === "supported" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {l.status === "supported" ? t("locale.status_supported") : t("locale.status_preview")}
            </span>
            {!l.reviewed && <span className="text-[10px] text-stone-500">{t("locale.unreviewed")}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function RegionFields() {
  const { t, country, setCountry, countries, region, timezone, setTimezone, currencyDisplay, setCurrencyDisplay } = useI18n();
  const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzs = [...new Set([...(region?.timezones || []), timezone, deviceTz].filter(Boolean))];
  const sel = "w-full h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm";
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <label className="text-xs font-bold text-stone-600">{t("locale.country")}
        <select value={country} onChange={(e) => setCountry(e.target.value)} className={sel} data-testid="locale-country-select">
          {(countries.length ? countries : [{ code: "IN", name: "India" }]).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>
      <label className="text-xs font-bold text-stone-600">{t("locale.timezone")}
        <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={sel} data-testid="locale-tz-select">
          {tzs.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </label>
      <label className="text-xs font-bold text-stone-600">{t("locale.currency")}
        <select value={currencyDisplay} onChange={(e) => setCurrencyDisplay(e.target.value)} className={sel} data-testid="locale-currency-select">
          <option value="source">{t("locale.currency_source")}</option>
          <option value="local">{t("locale.currency_local")}</option>
        </select>
      </label>
    </div>
  );
}

function FormatPreview() {
  const { t, lang, country, timezone, region, currencyDisplay } = useI18n();
  const [fx, setFx] = useState(null);
  const quote = region?.currency || "INR";
  useEffect(() => {
    setFx(null);
    if (currencyDisplay !== "local" || quote === "INR") return;
    api.get("/regions/fx", { params: { base: "INR", quote } }).then((r) => setFx(r.data)).catch(() => setFx({ available: false }));
  }, [currencyDisplay, quote]);
  const o = { lang, country, tz: timezone };
  const conv = convertMoney(2499, fx);
  const now = new Date().toISOString();
  return (
    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-800 space-y-1" data-testid="locale-preview">
      <div className="text-[10px] font-black uppercase tracking-widest text-stone-400">{t("locale.sample")}</div>
      <div data-testid="locale-preview-date">{formatDateTime(now, { ...o, showTz: true })}</div>
      <div data-testid="locale-preview-number">{formatNumber(1234567.89, o)}</div>
      <div data-testid="locale-preview-money">{formatMoney(2499, { ...o, currency: "INR" })}
        {conv && <span className="text-stone-500"> ≈ {formatMoney(conv.amount, { ...o, currency: conv.currency })} · {t("locale.fx_asof", { time: formatDateTime(conv.as_of, o) })}</span>}
        {fx && !fx.available && <span className="text-amber-700 text-xs" data-testid="locale-fx-unavailable"> · {t("locale.fx_unavailable")}</span>}
      </div>
      <div data-testid="locale-preview-phone" dir="ltr" className="text-start">{formatPhone("9876543210987".slice(0, (region?.phone_groups || [5, 5]).reduce((a, b) => a + b, 0)), o)}</div>
      {region && <div className="text-xs text-stone-500">{t("locale.emergency")}: <b dir="ltr">{region.emergency}</b></div>}
    </div>
  );
}

function Capabilities() {
  const { t, region } = useI18n();
  if (!region) return null;
  const tone = { available: "text-emerald-700", limited: "text-amber-700", unavailable: "text-stone-400" };
  return (
    <div data-testid="locale-capabilities">
      <div className="text-[10px] font-black uppercase tracking-widest text-stone-400 mb-1">{t("locale.services")}</div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {Object.entries(region.capabilities).map(([k, v]) => (
          <li key={k} className="flex justify-between gap-2" data-testid={`locale-cap-${k}`}>
            <span className="text-stone-700">{t(`locale.cap.${k}`)}</span>
            <span className={`font-bold ${tone[v]}`}>{t(`locale.cap_${v}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LocaleDialog({ open, onOpenChange }) {
  const { t, info } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="f2n-locale-dialog z-[120] max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="locale-dialog">
        <DialogHeader>
          <DialogTitle>{t("locale.title")}</DialogTitle>
          <DialogDescription>{t("locale.country_hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <LanguageList />
          {info.status !== "supported" && <p className="text-xs text-amber-800 bg-amber-50 rounded-xl p-2" data-testid="locale-preview-notice">{t("locale.preview_notice")}</p>}
          <RegionFields />
          <FormatPreview />
          <Capabilities />
          <p className="text-[11px] text-stone-500">{t("locale.legal_english")}</p>
          <button onClick={() => onOpenChange(false)} data-testid="locale-done-btn"
                  className="w-full h-10 rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white font-bold transition-colors">
            {t("locale.done")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
